import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";
import { useForm } from "react-hook-form";
import {
  useGetMerchantPaymentAccounts,
  useGetMerchantWalletBalance,
  useMerchantDisbursementsCharges,
  useMerchantDisbursementLimits,
  useMerchantInitiateWithdrawal,
  useAuthorizeDisbursement,
  useResendDisbursementOtp,
  useGetDisbursement,
} from "../../../hooks/queries/useMerchantQueries";
import { usePollDisbursement } from "../shared/usePollDisbursement";
import { useOtpInactivityTimer } from "../../shared/useOtpInactivityTimer";
import { useResendTimer } from "../../../hooks/useResendTimer";
import useFundsTransferStore from "../../../stores/fundsTransferStore";
import { formatKES } from "../../../utils/transactionutils";
import { toastService } from "../../../providers";
import { extractAxiosErrorMessage } from "../../../services/utils/errorUtils";
import {
  isInvalidOtpMessage,
  applyOtpBlock,
  applyPinLockedBlock,
} from "../../shared/otpErrorUtils";
import {
  useFundsTransferOtpBlockStore,
  usePinLockedStore,
} from "../../../stores/useOtpBlockStore";
import {
  parsePinCode,
  formatPinInvalidMessage,
} from "../../../services/utils/pinAuthUtils";
import { authQueryKeys } from "../../../hooks/queries/useAuthQueries";
import {
  formatMobileMoneyAccount,
  formatBankAccount,
  formatWalletAccount,
} from "../utils/accountFormatters";
import { PROVIDER_LOGOS } from "../../shared/mobileMoneyProviders";
import { useAirtelEnabled } from "../../../hooks/useAirtelEnabled";
import {
  PAYOUT_ACCOUNT_TYPES,
  WITHDRAWAL_TYPE_LABEL,
  DISBURSEMENT_ACTION_CODES,
  DISBURSEMENT_PIN_ERROR_CODES,
  TRANSACTION_NOT_FOUND_MESSAGE,
  TERMINAL_DISBURSEMENT_STATUSES,
} from "../config/fundsTransferConfig";

function getAccountTypesForWithdrawalType(
  accountsData,
  withdrawalType,
  airtelEnabled,
) {
  if (!accountsData) return [];
  switch (withdrawalType) {
    case PAYOUT_ACCOUNT_TYPES.MOBILE_MONEY:
      return (accountsData.mobileMoneyAccounts ?? [])
        .filter((acc) => airtelEnabled || acc.provider !== "Airtel")
        .map((acc) => ({
          id: acc.id,
          display: formatMobileMoneyAccount(acc),
          details: acc,
        }));
    case PAYOUT_ACCOUNT_TYPES.BANK:
      return (accountsData.bankAccounts ?? []).map((acc) => ({
        id: acc.id,
        display: formatBankAccount(acc),
        details: acc,
      }));
    case PAYOUT_ACCOUNT_TYPES.WALLET:
      return (accountsData.walletAccounts ?? []).map((acc) => ({
        id: acc.id,
        display: formatWalletAccount(acc),
        details: acc,
      }));
    default:
      return [];
  }
}

export function useWithdrawalFlow(withdrawalType) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    currentStep,
    otpSentTo,
    confirmationMode,
    transactionFee,
    exciseDuty,
    totalAmount,
    transactionId,
    remarks,
    status,
    createdDate,
    newBalance,
  } = useFundsTransferStore(
    useShallow(
      ({
        currentStep,
        otpSentTo,
        confirmationMode,
        transactionFee,
        exciseDuty,
        totalAmount,
        transactionId,
        remarks,
        status,
        createdDate,
        newBalance,
      }) => ({
        currentStep,
        otpSentTo,
        confirmationMode,
        transactionFee,
        exciseDuty,
        totalAmount,
        transactionId,
        remarks,
        status,
        createdDate,
        newBalance,
      }),
    ),
  );

  const setCurrentStep = useFundsTransferStore((state) => state.setCurrentStep);

  const { withdrawalAmount, selectedPayoutAccountId } =
    useFundsTransferStore.getState();

  const { control, watch, trigger } = useForm({
    defaultValues: {
      amount: withdrawalAmount || "",
      selectedPayoutAccountId: selectedPayoutAccountId || "",
    },
    mode: "onTouched",
  });

  const watchedAmount = watch("amount");
  const watchedSelectedPayoutAccountId = watch("selectedPayoutAccountId");

  const {
    control: otpControl,
    handleSubmit: handleOtpSubmit,
    watch: watchOtp,
  } = useForm({ defaultValues: { otp: "" } });

  const otpValue = watchOtp("otp") ?? "";

  const {
    control: pinControl,
    handleSubmit: handlePinSubmit,
    watch: watchPin,
    setError: setPinError,
    reset: resetPinForm,
  } = useForm({ defaultValues: { pin: "" } });

  const pinValue = watchPin("pin") ?? "";

  const { data: walletBalanceData, isError: isBalanceError } =
    useGetMerchantWalletBalance();
  const walletBalance = walletBalanceData?.balance ?? null;

  const {
    data: accountsData,
    isLoading: isAccountsLoading,
    isFetching: isAccountsFetching,
    isError: isAccountsError,
    refetch: refetchAccounts,
  } = useGetMerchantPaymentAccounts();

  const { mutate: fetchCharges, isPending: isLoadingCharges } =
    useMerchantDisbursementsCharges();

  const { mutate: initiateWithdrawal, isPending: isInitiating } =
    useMerchantInitiateWithdrawal();

  const { mutate: authorizeDisburse, isPending: isAuthorizing } =
    useAuthorizeDisbursement();

  const { mutate: resendOtp } = useResendDisbursementOtp();

  const { isPolling, startPolling } = usePollDisbursement();

  // True from the very first render whenever we land on step 3 with an OTP
  // authorize attempt already submitted (e.g. after a refresh mid-request or
  // mid-polling) — set via a lazy initializer so there's no flash of the
  // interactive OTP form before the rehydration check below resolves.
  const [isRehydrating, setIsRehydrating] = useState(() => {
    const state = useFundsTransferStore.getState();
    return (
      state.currentStep === 3 &&
      !!state.transactionId &&
      state.otpAuthorizationSubmitted
    );
  });

  // Fetches on Step 4 (data not needed until the result screen — handles the
  // edge case where the status resolves between the last poll and the step 4
  // transition) and while rehydrating step 3 after a refresh, to confirm the
  // real transaction status before resuming polling or showing the OTP form.
  const {
    data: disbursementData,
    isLoading: isLoadingDisbursement,
    isError: isDisbursementError,
    refetch: refetchDisbursement,
  } = useGetDisbursement(
    currentStep >= 4 || isRehydrating ? transactionId : null,
    { enablePolling: false },
  );

  useEffect(() => {
    if (!disbursementData) return;
    useFundsTransferStore.getState().setDisbursementResult(disbursementData);
    if (!isRehydrating) return;
    setIsRehydrating(false);
    if (TERMINAL_DISBURSEMENT_STATUSES.has(disbursementData.status)) {
      useFundsTransferStore.getState().setCurrentStep(4);
    } else {
      startPolling();
    }
  }, [disbursementData, isRehydrating, startPolling]);

  const {
    secondsLeft: resendSecondsLeft,
    startTimer: startResendTimer,
    isActive: isResendCooldownActive,
  } = useResendTimer(60);

  const handleOtpTimeout = useCallback(() => {
    navigate("/funds-transfer", {
      state: { fundsTransferBannerReason: "otp_session_expired" },
    });
  }, [navigate]);

  const { resetTimer: resetOtpTimer } = useOtpInactivityTimer({
    otpValue: confirmationMode === "PIN" ? pinValue : otpValue,
    isAuthorizing,
    isPolling,
    isRehydrating,
    isActive: currentStep === 3,
    onTimeout: handleOtpTimeout,
  });

  // Step 3 is "busy" (PIN input/submit disabled) for any of these reasons;
  // kept separate from the overlay's visibility condition, which has an
  // additional exception for the rehydration-error fallback screen.
  const isStepThreeBusy = isAuthorizing || isPolling || isRehydrating;

  const airtelEnabled = useAirtelEnabled();
  const accountOptions = getAccountTypesForWithdrawalType(
    accountsData,
    withdrawalType,
    airtelEnabled,
  );

  const payoutAccountOptions = useMemo(
    () =>
      accountOptions.map((acc) => ({
        value: acc.id,
        label: acc.display,
        disabled: !acc?.details?.active,
        logo: PROVIDER_LOGOS[acc.details?.provider] ?? null,
      })),
    [accountOptions],
  );

  const selectedPayoutAccount = useMemo(() => {
    if (!watchedSelectedPayoutAccountId) return null;
    return (
      accountOptions.find((acc) => acc.id === watchedSelectedPayoutAccountId) ??
      null
    );
  }, [watchedSelectedPayoutAccountId, accountOptions]);

  const limitsPayload =
    withdrawalType === PAYOUT_ACCOUNT_TYPES.MOBILE_MONEY
      ? { provider: selectedPayoutAccount?.details?.provider ?? null }
      : withdrawalType === PAYOUT_ACCOUNT_TYPES.BANK
        ? { paymentChannel: "Bank" }
        : {};

  const { data: disbursementLimits, isLoading: isLimitsLoading } =
    useMerchantDisbursementLimits(limitsPayload);

  const summaryItems = [
    {
      label: "Withdrawal Type",
      value: WITHDRAWAL_TYPE_LABEL[withdrawalType] ?? "—",
    },
    {
      label: "Destination Account",
      value: selectedPayoutAccount
        ? (selectedPayoutAccount.details?.accountName ??
          selectedPayoutAccount.display)
        : "—",
    },
    {
      label: "Amount",
      value: formatKES(watchedAmount),
    },
    {
      label: "Available Balance",
      value: formatKES(walletBalance),
    },
  ];

  const isFormLocked =
    isAccountsLoading ||
    isAccountsFetching ||
    isLoadingCharges ||
    payoutAccountOptions.length === 0;

  useEffect(() => {
    useFundsTransferStore.getState().setWithdrawalDraft({
      withdrawalAmount: watchedAmount,
      selectedPayoutAccountId: watchedSelectedPayoutAccountId,
    });
  }, [watchedAmount, watchedSelectedPayoutAccountId]);

  const handleStep1Next = async () => {
    const isValid = await trigger(["amount", "selectedPayoutAccountId"]);
    if (!isValid) return;

    fetchCharges(
      {
        amount: Number(watchedAmount),
        payoutAccountId: watchedSelectedPayoutAccountId,
        payoutAccountType: withdrawalType,
      },
      {
        onSuccess: (data) => {
          useFundsTransferStore.getState().setWithdrawalCharges({
            transactionFee: data.charges,
            exciseDuty: data.exciseDuty,
            totalAmount: data.totalAmount,
            newBalance: data.newBalance,
          });
          setCurrentStep(2);
        },
        onError: (error) => {
          toastService.error(extractAxiosErrorMessage(error));
        },
      },
    );
  };

  const handleStep2Back = () => {
    useFundsTransferStore.getState().setCurrentStep(1);
  };

  const handleStep2Confirm = () => {
    const { withdrawalAmount, selectedPayoutAccountId } =
      useFundsTransferStore.getState();

    initiateWithdrawal(
      {
        amount: Number(withdrawalAmount),
        payoutAccountId: selectedPayoutAccountId,
        payoutAccountType: withdrawalType,
      },
      {
        onSuccess: (data) => {
          const nextConfirmationMode =
            data.action?.code ===
            DISBURSEMENT_ACTION_CODES.PIN_CONFIRMATION_REQUIRED
              ? "PIN"
              : "OTP";
          useFundsTransferStore.getState().setWithdrawalInitiation({
            transactionId: data.transactionId,
            otpSentTo: data.otpSentTo,
            confirmationMode: nextConfirmationMode,
          });
          startResendTimer();
          setCurrentStep(3);
        },
        onError: (error) => {
          toastService.error(extractAxiosErrorMessage(error));
        },
      },
    );
  };

  const handleResendOtp = () => {
    const { transactionId } = useFundsTransferStore.getState();
    if (!transactionId) return;
    resetOtpTimer();
    startResendTimer();
    resendOtp(transactionId, {
      onError: () => {
        toastService.error("Failed to resend code. Please try again.");
      },
    });
  };

  const handlePinAuthorizeSubmit = ({ pin }) => {
    useFundsTransferStore.getState().setOtpAuthorizationSubmitted(true);
    authorizeDisburse(
      { transactionId, pin },
      {
        onSuccess: (data) => {
          const code = parsePinCode(null, data);

          if (code === DISBURSEMENT_PIN_ERROR_CODES.PIN_INVALID) {
            useFundsTransferStore.getState().setOtpAuthorizationSubmitted(false);
            resetPinForm({ pin: "" });
            setPinError("pin", { message: formatPinInvalidMessage(data) });
            return;
          }

          if (code === DISBURSEMENT_PIN_ERROR_CODES.PIN_LOCKED) {
            applyPinLockedBlock(
              data,
              usePinLockedStore,
              DISBURSEMENT_PIN_ERROR_CODES.PIN_LOCKED,
            );
            queryClient.invalidateQueries({
              queryKey: authQueryKeys.currentUser(),
            });
            navigate("/funds-transfer");
            return;
          }

          if (code === DISBURSEMENT_PIN_ERROR_CODES.PIN_SETUP_REQUIRED) {
            toastService.error(
              "Your PIN is not set up. Please contact support.",
            );
            navigate("/funds-transfer");
            return;
          }

          queryClient.invalidateQueries({
            queryKey: authQueryKeys.currentUser(),
          });
          useFundsTransferStore.getState().setDisbursementResult(data);
          startPolling();
        },
        onError: (err) => {
          if (!err.response) {
            useFundsTransferStore.getState().setOtpAuthorizationSubmitted(false);
            toastService.error(
              "Request failed. Please check your connection and try again.",
            );
            return;
          }

          const code = parsePinCode(err, null);

          if (code === TRANSACTION_NOT_FOUND_MESSAGE) {
            toastService.error(
              "This withdrawal request is no longer valid. Please start again.",
            );
            navigate("/funds-transfer");
            return;
          }

          toastService.error(extractAxiosErrorMessage(err));
        },
      },
    );
  };

  const handleOtpAuthorizeSubmit = ({ otp }) => {
    useFundsTransferStore.getState().setOtpAuthorizationSubmitted(true);
    authorizeDisburse(
      { otpCode: otp },
      {
        onSuccess: (data) => {
          useFundsTransferStore.getState().setDisbursementResult(data);
          startPolling();
        },
        onError: (err) => {
          if (!err.response) {
            useFundsTransferStore.getState().setOtpAuthorizationSubmitted(false);
            toastService.error(
              "Request failed. Please check your connection and try again.",
            );
            return;
          }
          const message = extractAxiosErrorMessage(err);
          if (isInvalidOtpMessage(message)) {
            useFundsTransferStore.getState().setOtpAuthorizationSubmitted(false);
            toastService.error(
              "Invalid code entered. Please check and try again.",
            );
            return;
          }
          if (message.toLowerCase().includes("validation blocked")) {
            useFundsTransferStore.getState().setOtpAuthorizationSubmitted(false);
            applyOtpBlock(err.response, useFundsTransferOtpBlockStore);
            navigate("/funds-transfer");
            return;
          }
          useFundsTransferStore.getState().setWithdrawalFailed(message);
          useFundsTransferStore.getState().setCurrentStep(4);
        },
      },
    );
  };

  const handleStep3Submit =
    confirmationMode === "PIN"
      ? handlePinAuthorizeSubmit
      : handleOtpAuthorizeSubmit;

  return {
    currentStep,
    control,
    payoutAccountOptions,
    isAccountsLoading,
    isAccountsFetching,
    isAccountsError,
    refetchAccounts,
    walletBalance,
    isBalanceError,
    isLoadingCharges,
    isFormLocked,
    disbursementLimits,
    isLimitsLoading,
    onNext: handleStep1Next,
    selectedPayoutAccount,
    withdrawalAmount: watchedAmount,
    watchedSelectedPayoutAccountId,
    transactionFee,
    exciseDuty,
    totalAmount,
    handleStep2Back,
    handleStep2Confirm,
    isInitiating,
    otpControl,
    handleOtpSubmit,
    otpValue,
    otpSentTo,
    pinControl,
    handlePinSubmit,
    pinValue,
    confirmationMode,
    handleStep3Submit,
    isAuthorizing,
    handleResendOtp,
    resendSecondsLeft,
    isResendCooldownActive,
    isPolling,
    isRehydrating,
    isStepThreeBusy,
    summaryItems,
    transactionId,
    remarks,
    status,
    createdDate,
    newBalance,
    isLoadingDisbursement,
    isDisbursementError,
    refetchDisbursement,
  };
}
